// --- Dark mode CSS generation helpers ---

export function hexToRgb(
  hex: string
): { r: number; g: number; b: number } | null {
  const h = hex.replace("#", "").trim();
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return { r, g, b };
  }
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { r, g, b };
  }
  return null;
}

export function parseRgb(
  input: string
): { r: number; g: number; b: number; a?: number } | null {
  const m = input.match(
    /rgba?\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)/i
  );
  if (!m) return null;
  const r = Math.min(255, parseInt(m[1], 10));
  const g = Math.min(255, parseInt(m[2], 10));
  const b = Math.min(255, parseInt(m[3], 10));
  const a =
    m[4] !== undefined ? Math.max(0, Math.min(1, parseFloat(m[4]))) : undefined;
  return { r, g, b, a };
}

export function luminance({
  r,
  g,
  b,
}: {
  r: number;
  g: number;
  b: number;
}): number {
  const srgb = [r, g, b].map((v) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

export function toCssColor({
  r,
  g,
  b,
  a,
}: {
  r: number;
  g: number;
  b: number;
  a?: number;
}): string {
  if (a === undefined) return `rgb(${r}, ${g}, ${b})`;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function parseColor(
  value: string
): { r: number; g: number; b: number; a?: number } | null {
  const v = value.trim().toLowerCase();
  if (v === "white") return { r: 255, g: 255, b: 255 };
  if (v === "black") return { r: 0, g: 0, b: 0 };
  if (v.startsWith("#")) return hexToRgb(v);
  const rgb = parseRgb(v);
  if (rgb) return rgb;
  return null; // skip hsl()/named colors we don't understand
}

export function adjustForDarkMode(prop: string, value: string): string | null {
  const col = parseColor(value);
  if (!col) return null;
  const L = luminance(col);
  // Heuristics by property type
  if (/^(color|fill|stroke)$/i.test(prop)) {
    // Make text/graphics light on dark backgrounds
    const target =
      L < 0.7 ? { r: 235, g: 235, b: 235 } : { r: 245, g: 245, b: 245 };
    return toCssColor({ ...target, a: col.a });
  }
  if (/^(background|background-color)$/i.test(prop)) {
    // Turn light backgrounds into dark surfaces
    if (L > 0.85) return "rgb(28, 28, 28)";
    if (L > 0.7) return "rgb(40, 40, 40)";
    // keep darker backgrounds
    return toCssColor(col);
  }
  if (
    /border(\-top|\-right|\-bottom|\-left)?\-color/i.test(prop) ||
    /^outline-color$/i.test(prop)
  ) {
    if (L > 0.8) return "rgb(80, 80, 80)";
    return toCssColor(col);
  }
  return null;
}

/**
 * Transform a block of (already scoped) CSS into a set of dark-mode overrides.
 * We prefix each selector with `.dark` (Tailwind convention) and adjust colors.
 */
export function makeDarkModeCss(scopedCss: string): string {
  const blocks = scopedCss.split("}");
  const darkParts: string[] = [];
  for (const raw of blocks) {
    const part = raw.trim();
    if (!part) continue;
    const [sel, decl] = part.split("{");
    if (!sel || !decl) continue;
    const selector = sel.trim();
    const rules = decl
      .split(";")
      .map((r) => r.trim())
      .filter(Boolean);

    const adjusted: string[] = [];
    for (const rule of rules) {
      const m = rule.match(/^(.*?)\s*:\s*(.*?)\s*(\!important)?$/);
      if (!m) continue;
      const prop = m[1].trim();
      const val = m[2].trim();
      const origImportant = m[3] ? " !important" : "";

      if (
        /^(color|fill|stroke|background|background-color|outline-color|border(\-top|\-right|\-bottom|\-left)?\-color)$/i.test(
          prop
        )
      ) {
        const adj = adjustForDarkMode(prop, val);
        if (adj) {
          adjusted.push(`${prop}: ${adj} !important`);
          continue;
        }
      }
      // keep other properties as-is to preserve layout (margins, fonts, etc.)
      adjusted.push(`${prop}: ${val}${origImportant}`);
    }

    if (adjusted.length > 0) {
      darkParts.push(`.dark ${selector}{${adjusted.join(";")}}`);
    }
  }
  return darkParts.join("\n");
}
